import { Injectable } from '@angular/core';
import { DataRole } from '@mode-switch/express';
import { View } from '@mode-switch/model';
import { EnhancedDatasetResult, SwitchAttr, SwitchViewDomain } from '@mode/shared/contract-common';
import { SwitchViewClientService } from '@mode/shared/data-access-webapp';
import { Switch } from '@mode/shared/util-switch';
import { SwitchViewActions, SwitchViewFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { DatasetResourceColumn } from 'libs/shared/contract-common/src/lib/data-view';
import { ModeRole } from 'libs/shared/contract-common/src/lib/flamingo.types';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isPresent, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { SwitchResourceHelpers } from '../..';
import { DomainAppState } from '../switch-view-domain.types';
import { getEnhancedDatasetResultBySwitchViewToken } from './switch-view.selectors';

@Injectable()
export class SwitchViewService implements SwitchViewFacade {
  constructor(
    private store: Store<DomainAppState>,
    private switchViewClientService: SwitchViewClientService,
    private metaTags: MetaTagsService
  ) {}

  public getEnhancedDataseResult(switchViewToken: string): Observable<EnhancedDatasetResult> {
    return this.store.select(getEnhancedDatasetResultBySwitchViewToken(switchViewToken)).pipe(filter(isPresent));
  }

  public loadSwitchViewDefinitionRoute(switchViewToken: string): void {
    this.store.dispatch(SwitchViewActions.loadSwitchViewDefinitonRoute({ switchViewToken }));
  }

  public queryRawDataset(queryToken: string) {
    this.store.dispatch(SwitchViewActions.queryRawDataset({ queryToken: queryToken }));
  }
  public loadImportedDatasetSuccess(
    dataView: SwitchViewDomain.DataView,
    shareLink?: string,
    mostRecentReportRunToken?: string
  ) {
    this.store.dispatch(
      SwitchViewActions.loadImportedDatasetSuccess({ dataView, shareLink, mostRecentReportRunToken })
    );
  }

  public createSwitchView(
    queryToken: string,
    queryRunToken: string,
    metaColumns: DatasetResourceColumn[],
    attrs: ReadonlyArray<View.Attr<ModeRole>>
  ) {
    return Switch.Views.make(queryToken, queryRunToken, metaColumns, attrs);
  }

  public getSwitchView(switchViewToken: string): Observable<SwitchViewDomain.DataView> {
    return this.switchViewClientService.getSwitchView(switchViewToken);
  }

  public makeView(switchView: View<DataRole>) {
    return Switch.Models.makeFromView(switchView.name, switchView);
  }

  public makeAttrs(attrs: SwitchAttr[]) {
    return SwitchResourceHelpers.makeAttrsFromFeatureState(attrs);
  }

  /**
   * @description Fetches the DataView for a given DatasetReport
   * DatasetReports contain a single DataView
   * @param {string} datasetToken - datasetToken for a DatasetReport
   * @returns a single DataView for the DatasetReport
   */
  public getDataset(datasetToken: string) {
    return this.switchViewClientService
      .getViewsInDatasetReport(
        this.metaTags.getTag(MetaTags.OwnerUsername) ??
          'orgId missing from meta tags! It should be in the owner-username tag',
        datasetToken
      )
      .pipe(map(([dataset]) => dataset));
  }

  /**
   * @description Similar to getDataset, here the datasetToken is the reportToken as a notebook cell dataset does not have a DatasetReport
   *
   * Fetches the DataView for the specified switchViewToken from the loaded DataViews
   * @param {string} datasetToken - datasetToken which is the reportToken of the current report
   * @param {string} switchViewToken - switchViewToken to fetch the required DataView
   * @returns a single DataView based on the switchViewToken for the Report
   */
  public getNotebookCellDataset(datasetToken: string, switchViewToken: string) {
    return this.switchViewClientService
      .getViewsInDatasetReport(
        this.metaTags.getTag(MetaTags.OwnerUsername) ??
          'orgId missing from meta tags! It should be in the owner-username tag',
        datasetToken
      )
      .pipe(map((datasets) => datasets.find((ds) => ds.switchView.token === switchViewToken) ?? datasets[0]));
  }

  public googleDatasetSyncSwitchViewCompleted(dataView: SwitchViewDomain.DataView) {
    this.store.dispatch(SwitchViewActions.pollingSwitchViewCompleted({ dataView }));
  }
}
